import { Controller } from '@hotwired/stimulus'
import { format as formatTz, utcToZonedTime } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import formatDateFns from 'date-fns/format'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import { getTimestampFormat } from '../../utils/utils'
import {
  trans,
  AUDIT_TIMESTAMP,
  AUDIT_ACTOR,
  AUDIT_EVENT_TYPE,
  AUDIT_FIELD,
  AUDIT_BEFORE,
  AUDIT_AFTER,
  AUDIT_UNAVAILABLE,
  AUDIT_PHOTO_REMOVED,
  AUDIT_EVENT_RECORD_CREATED,
  AUDIT_EVENT_RECORD_VIEWED_TIER2,
  AUDIT_EVENT_RECORD_VIEWED_TIER3,
  AUDIT_EVENT_RECORD_EDITED,
  AUDIT_EVENT_RECORD_DELETED,
  AUDIT_EVENT_ACCESS_GRANT_REQUESTED,
  AUDIT_EVENT_ACCESS_GRANT_APPROVED,
  AUDIT_EVENT_ACCESS_GRANT_DENIED,
  AUDIT_EVENT_PDF_EXPORTED,
  AUDIT_EVENT_DUPLICATE_MATCH_SURFACED,
  FULL_NAME,
  DATE_OF_BIRTH,
  STATUS,
  SOURCE,
  SOURCE_OTHER,
  CRIMINAL_HISTORY_NARRATIVE,
  THREAT_ASSESSMENT,
  ACTIONS_TAKEN,
  ALIASES,
  PHONE_NUMBERS,
  EMAILS,
  ADDRESSES,
  SOCIAL_MEDIA_HANDLES,
  CRIMINAL_HISTORY_FLAGS,
  FILTER_BANNED,
  STATUS_HIGH_RISK,
  STATUS_MEDIUM_RISK,
  STATUS_LOW_RISK,
  CRIMINAL_FLAG_VIOLENT_OFFENDER,
  CRIMINAL_FLAG_VIOLENT_RHETORIC,
  PRINT_SOURCE_POLICE_TIP,
  PRINT_SOURCE_OTHER,
  PHOTOS,
  PRIMARY_PHOTO,
  NOTES,
  YEAR_RANGE
} from '../../translator'

declare const $: (selector: string | HTMLElement) => { modal: (action: string) => void }
declare const accountDateFormat: string
declare const accountIsUseAmPm: string
declare const accountTimezone: string
declare const selectedTimezone: string

interface AuditEvent {
  event_type: string
  actor_profile_id: string
  actor_display_name: string | null
  event_timestamp: string
  reason: string | null
  before_values: Record<string, unknown> | null
  after_values: Record<string, unknown> | null
}

interface AuditResponse {
  result: AuditEvent[]
}

// The shape AssociatedPersonService::dobAuditSnapshot() writes for a dateOfBirth change.
interface DobComponents {
  year_from: number | null
  year_to: number | null
  month: number | null
  day: number | null
}

const PAGE_SIZE = 20
const XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }

const EVENT_TYPE_LABELS: Record<string, typeof AUDIT_EVENT_RECORD_CREATED> = {
  record_created: AUDIT_EVENT_RECORD_CREATED,
  record_viewed_tier2: AUDIT_EVENT_RECORD_VIEWED_TIER2,
  record_viewed_tier3: AUDIT_EVENT_RECORD_VIEWED_TIER3,
  record_edited: AUDIT_EVENT_RECORD_EDITED,
  record_deleted: AUDIT_EVENT_RECORD_DELETED,
  access_grant_requested: AUDIT_EVENT_ACCESS_GRANT_REQUESTED,
  access_grant_approved: AUDIT_EVENT_ACCESS_GRANT_APPROVED,
  access_grant_denied: AUDIT_EVENT_ACCESS_GRANT_DENIED,
  pdf_exported: AUDIT_EVENT_PDF_EXPORTED,
  duplicate_match_surfaced: AUDIT_EVENT_DUPLICATE_MATCH_SURFACED
}

const FIELD_LABELS: Record<string, Parameters<typeof trans>[0]> = {
  fullName: FULL_NAME,
  dateOfBirth: DATE_OF_BIRTH,
  status: STATUS,
  source: SOURCE,
  sourceOther: SOURCE_OTHER,
  criminalHistoryNarrative: CRIMINAL_HISTORY_NARRATIVE,
  threatAssessment: THREAT_ASSESSMENT,
  actionsTaken: ACTIONS_TAKEN,
  aliases: ALIASES,
  phoneNumbers: PHONE_NUMBERS,
  emails: EMAILS,
  addresses: ADDRESSES,
  socialMediaHandles: SOCIAL_MEDIA_HANDLES,
  criminalHistoryFlags: CRIMINAL_HISTORY_FLAGS,
  isBanned: FILTER_BANNED,
  photos: PHOTOS,
  primaryPhoto: PRIMARY_PHOTO,
  personNotes: NOTES
}

const VALUE_LABELS: Record<string, Record<string, Parameters<typeof trans>[0]>> = {
  status: {
    high_risk: STATUS_HIGH_RISK,
    medium_risk: STATUS_MEDIUM_RISK,
    low_risk: STATUS_LOW_RISK
  },
  criminalHistoryFlags: {
    violent_offender: CRIMINAL_FLAG_VIOLENT_OFFENDER,
    violent_rhetoric: CRIMINAL_FLAG_VIOLENT_RHETORIC
  },
  source: {
    police_tip: PRINT_SOURCE_POLICE_TIP,
    other: PRINT_SOURCE_OTHER
  }
}

export default class AuditTrailController extends Controller {
  static values = {
    apId: String
  }

  static targets = ['list', 'modal', 'modalList', 'loadMore']

  declare readonly apIdValue: string
  declare readonly listTarget: HTMLElement
  declare readonly modalTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly modalListTarget: HTMLElement
  declare readonly hasModalListTarget: boolean
  declare readonly loadMoreTarget: HTMLButtonElement
  declare readonly hasLoadMoreTarget: boolean

  private cursor: string | null = null
  private hasMore = false

  connect (): void {
    // eslint-disable-next-line no-void
    void this.loadInitial()
  }

  private async loadInitial (): Promise<void> {
    try {
      const events = await this.fetchEvents(null)
      this.listTarget.innerHTML = this.renderTable(events)
      this.bindPhotoErrorFallbacks(this.listTarget)
      this.trackPagination(events)
    } catch {
      this.listTarget.innerHTML = this.renderError()
    }
  }

  openFull (): void {
    if (!this.hasModalListTarget) {
      return
    }

    this.modalListTarget.innerHTML = this.listTarget.innerHTML
    // The copied markup carries lightGallery's lg-uid stamp from the inline list; clearing it
    // lets the photo-gallery controllers re-bind to the modal's own copies of the thumbnails.
    this.modalListTarget.querySelectorAll('[lg-uid]').forEach(el => el.removeAttribute('lg-uid'))
    this.bindPhotoErrorFallbacks(this.modalListTarget)
    this.toggleLoadMore()

    if (this.hasModalTarget) {
      $(this.modalTarget).modal('show')
    }
  }

  async loadMore (): Promise<void> {
    if (this.cursor === null) {
      return
    }

    try {
      const events = await this.fetchEvents(this.cursor)
      const tbody = this.modalListTarget.querySelector('tbody')
      if (tbody !== null) {
        tbody.insertAdjacentHTML('beforeend', this.renderRows(events))
      }
      this.bindPhotoErrorFallbacks(this.modalListTarget)
      this.trackPagination(events)
      this.toggleLoadMore()
    } catch {
      this.modalListTarget.insertAdjacentHTML('beforeend', this.renderError())
    }
  }

  private async fetchEvents (before: string | null): Promise<AuditEvent[]> {
    const url = before === null
      ? `/internal/api/associated-person/${this.apIdValue}/audit`
      : `/internal/api/associated-person/${this.apIdValue}/audit?before=${encodeURIComponent(before)}`

    const response = await fetchWithHandlingLoginRedirect(url, { headers: XHR_HEADERS })
    const data = await response.json() as AuditResponse

    return data.result ?? []
  }

  private trackPagination (events: AuditEvent[]): void {
    this.hasMore = events.length === PAGE_SIZE
    if (events.length > 0) {
      this.cursor = events[events.length - 1].event_timestamp
    }
  }

  private toggleLoadMore (): void {
    if (!this.hasLoadMoreTarget) {
      return
    }
    this.loadMoreTarget.classList.toggle('d-none', !this.hasMore)
  }

  private timestampHeader (): string {
    const label = trans(AUDIT_TIMESTAMP, {}, 'associated-persons')
    const tz = formatTz(utcToZonedTime(new Date(), accountTimezone), 'zzz', { timeZone: accountTimezone })
    return `${label} (${tz})`
  }

  private formatIn (date: Date, timezone: string, withTimezone: boolean): string {
    const dateFormat = accountDateFormat.replace('mm', 'MM')
    return formatTz(
      utcToZonedTime(date, timezone),
      `${dateFormat} ${getTimestampFormat(accountIsUseAmPm, withTimezone)}`,
      { timeZone: timezone }
    )
  }

  private formatTimestamp (timestamp: string): string {
    const date = parseISO(timestamp)
    const timezonesDiffer = selectedTimezone !== accountTimezone &&
      this.formatIn(date, accountTimezone, false) !== this.formatIn(date, selectedTimezone, false)

    if (!timezonesDiffer) {
      return this.formatIn(date, accountTimezone, false)
    }

    return `${this.formatIn(date, accountTimezone, true)}<br><small class="text-muted">${this.formatIn(date, selectedTimezone, true)}</small>`
  }

  private renderTable (events: AuditEvent[]): string {
    return `
      <table class="table table-sm mb-0">
        <thead>
          <tr>
            <th>${this.timestampHeader()}</th>
            <th>${trans(AUDIT_ACTOR, {}, 'associated-persons')}</th>
            <th style="white-space:nowrap;">${trans(AUDIT_EVENT_TYPE, {}, 'associated-persons')}</th>
            <th>${trans(AUDIT_FIELD, {}, 'associated-persons')}</th>
            <th>${trans(AUDIT_BEFORE, {}, 'associated-persons')}</th>
            <th>${trans(AUDIT_AFTER, {}, 'associated-persons')}</th>
          </tr>
        </thead>
        <tbody>${this.renderRows(events)}</tbody>
      </table>
    `
  }

  private renderRows (events: AuditEvent[]): string {
    return events.map((event, index) => {
      const colour = index % 2 === 0 ? 'table-light' : 'table-white'
      const details = this.detailCells(event, colour)
      const meta = `
        <td rowspan="${details.length}" class="${colour}">${this.formatTimestamp(event.event_timestamp)}</td>
        <td rowspan="${details.length}" class="${colour}">${escapeHtml(event.actor_display_name ?? event.actor_profile_id)}</td>
        <td rowspan="${details.length}" class="${colour}" style="white-space:nowrap;">${this.eventTypeLabel(event.event_type)}</td>
      `
      return details
        .map((detail, index) => `<tr>${index === 0 ? meta : ''}${detail}</tr>`)
        .join('')
    }).join('')
  }

  private eventTypeLabel (eventType: string): string {
    const label = EVENT_TYPE_LABELS[eventType]
    return label !== undefined ? trans(label, {}, 'associated-persons') : eventType
  }

  private fieldLabel (key: string): string {
    const label = FIELD_LABELS[key]
    return label !== undefined ? trans(label, {}, 'associated-persons') : key
  }

  private formatValue (key: string, value: unknown): string {
    if (value === null || value === undefined) {
      return ''
    }

    if (key === 'dateOfBirth') {
      return typeof value === 'string' ? this.formatDateOnly(value) : this.formatDobComponents(value as DobComponents)
    }

    const valueLabels = VALUE_LABELS[key]
    if (valueLabels === undefined) {
      return String(value)
    }

    return (Array.isArray(value) ? value : [value])
      .map(code => {
        const label = valueLabels[String(code)]
        return label !== undefined ? trans(label, {}, 'associated-persons') : String(code)
      })
      .join(', ')
  }

  /**
   * Renders a stored DOB — an RFC3339 date-time at midnight, since the column is date-only —
   * as a date with no time component, in the account's date format. The calendar date is
   * parsed straight out of the string so no timezone conversion can shift the day (a DOB is a
   * calendar date, not an instant). Unparseable values fall back to the raw string.
   */
  private formatDateOnly (value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (match === null) {
      return value
    }

    const [, year, month, day] = match
    const date = new Date(Number(year), Number(month) - 1, Number(day))

    return formatDateFns(date, accountDateFormat.replace('mm', 'MM'))
  }

  /**
   * Renders the four raw dob* components a change now carries (dobAuditSnapshot), at
   * whatever precision was actually recorded. Mirrors PartialDateOfBirth::format() so the
   * audit trail reads the same way the record itself displays.
   */
  private formatDobComponents (dob: DobComponents): string {
    const { year_from: yearFrom, year_to: yearTo, month, day } = dob
    const isSingleYear = yearFrom !== null && yearFrom === yearTo

    if (isSingleYear && month !== null && day !== null) {
      return formatDateFns(new Date(yearFrom, month - 1, day), accountDateFormat.replace('mm', 'MM'))
    }
    if (isSingleYear && month !== null) {
      return formatDateFns(new Date(yearFrom, month - 1, 1), 'MMMM yyyy')
    }
    if (isSingleYear) {
      return String(yearFrom)
    }
    if (month !== null && day !== null) {
      return formatDateFns(new Date(2000, month - 1, day), 'd MMMM')
    }
    if (yearFrom !== null && yearTo !== null) {
      return trans(YEAR_RANGE, { '%from%': String(yearFrom), '%to%': String(yearTo) }, 'associated-persons')
    }

    return ''
  }

  private detailCells (event: AuditEvent, colour: string): string[] {
    if (event.event_type === 'record_edited') {
      const before = event.before_values ?? {}
      const after = event.after_values ?? {}
      const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))

      if (keys.length > 0) {
        return keys.map(key => {
          if (key === 'photos' || key === 'primaryPhoto') {
            return `
              <td class="${colour}">${this.fieldLabel(key)}</td>
              <td class="${colour}">${this.renderAuditPhotos(before[key])}</td>
              <td class="${colour}">${this.renderAuditPhotos(after[key])}</td>
            `
          }

          return `
            <td class="${colour}">${this.fieldLabel(key)}</td>
            <td class="${colour}">${escapeHtml(this.formatValue(key, before[key]))}</td>
            <td class="${colour}">${escapeHtml(this.formatValue(key, after[key]))}</td>
          `
        })
      }
    }

    return [`<td colspan="3" class="${colour}">${escapeHtml(event.reason ?? '')}</td>`]
  }

  /**
   * Renders a photo set (before or after a change) as a lightgallery of clickable
   * thumbnails. The container is wired to the photo-gallery controller, which opens each
   * full-size on click. Empty sets render nothing.
   */
  private renderAuditPhotos (photos: unknown): string {
    if (!Array.isArray(photos) || photos.length === 0) {
      return ''
    }

    const anchors = photos.map(photo => {
      const url = typeof (photo as { url?: unknown }).url === 'string' ? (photo as { url: string }).url : ''
      const thumbnail = typeof (photo as { thumbnail_url?: unknown }).thumbnail_url === 'string' ? (photo as { thumbnail_url: string }).thumbnail_url : ''

      return `<a class="ap-photo__image d-inline-block mr-1 mb-1" href="${escapeHtml(url)}"><img src="${escapeHtml(thumbnail)}" alt="" class="rounded" style="width:48px;height:48px;object-fit:cover;" data-audit-photo></a>`
    }).join('')

    // Cap the gallery width so a photo-heavy edit wraps its thumbnails onto multiple rows instead
    // of ballooning the Before/After column, which would squeeze the text columns (notably Event
    // type) and collapse the table layout.
    return `<div class="ap-photo-gallery d-flex flex-wrap" style="max-width:200px;" data-controller="associated-persons--photo-gallery">${anchors}</div>`
  }

  /**
   * The "before" photo urls are presigned at edit time and the underlying media is hard-deleted
   * shortly afterwards (see photoAuditSnapshot), so a removed photo's thumbnail 404s or its
   * presigned link has expired by the time the trail is viewed. There is no reliable signal in
   * the feed, so we detect it at render time from the image load failure and swap the broken
   * thumbnail for a distinct "removed" icon.
   */
  private bindPhotoErrorFallbacks (root: HTMLElement): void {
    root.querySelectorAll<HTMLImageElement>('img[data-audit-photo]').forEach(img => {
      // A load error may already have fired before this binding runs (e.g. a cached failure).
      if (img.complete && img.naturalWidth === 0) {
        this.replaceWithRemovedIcon(img)

        return
      }

      img.addEventListener('error', () => this.replaceWithRemovedIcon(img), { once: true })
    })
  }

  private replaceWithRemovedIcon (img: HTMLImageElement): void {
    // Replace the whole anchor (its href points at the same dead media) when present.
    const target: Element = img.closest('a') ?? img
    if (target.parentElement === null) {
      return
    }

    const label = trans(AUDIT_PHOTO_REMOVED, {}, 'associated-persons')
    const icon = document.createElement('i')
    // fa-times-circle is the "removed" glyph available in this project's Font Awesome 5 Pro set
    // (FA6's image-circle-xmark does not exist here). Font Awesome icons are sized by font-size,
    // so set it explicitly to match the 48px thumbnails and stay visible/distinct.
    icon.className = 'fas fa-times-circle ap-audit-photo--removed d-inline-block mr-1 mb-1'
    icon.setAttribute('style', 'font-size:48px;line-height:1;color:#9CA3AF;')
    icon.setAttribute('role', 'img')
    icon.setAttribute('title', label)
    icon.setAttribute('aria-label', label)

    target.replaceWith(icon)
  }

  private renderError (): string {
    return `<p class="text-danger mb-0">${trans(AUDIT_UNAVAILABLE, {}, 'associated-persons')}</p>`
  }
}
